import { memo } from 'react';
import { spacing } from 'presentations/views/styles';
import {
  gray01,
  gray02,
  gray04,
  gray06,
} from 'presentations/views/styles/color';
import styled from '@emotion/styled';
import AllDayEventCell from '../AllDayEventCell';
import { useAllDayTimeGrid } from './hooks';
import { calendarNewEventPrefix } from '../DayTimeGrid/Event';
import { DateTime } from 'luxon';
import { Behavior, MergeableEventListItem, Schedule } from '../reducer';
import { DraggableCell } from '../DayTimeGrid/Slot';
import Icon from 'presentations/views/components/atoms/Icon';

export interface Props {
  startedAt: DateTime;
  onClickEvent?: (event: MergeableEventListItem, el: HTMLElement) => void;
  selectedEventId?: string;
  onUpdateSchedule?: (schedule: Schedule) => void;
}

const Component = memo((props: Props) => {
  const { state, actions } = useAllDayTimeGrid({
    startedAt: props.startedAt,
    onClickEvent: props.onClickEvent,
    onUpdateSchedule: props.onUpdateSchedule,
  });
  return (
    <Content maxCols={state.dates.length} maxRows={state.maxRow + 1}>
      {!state.dragDirection &&
        state.draggingGhostEvent &&
        state.draggingGhostEvent.isAllDay && (
          <GhostEventCell
            eventId={state.draggingGhostEvent.uid}
            x={state.draggingGhostEvent.x}
            xSpan={state.draggingGhostEvent.xSpan}
            y={state.draggingGhostEvent.y}
            ySpan={state.draggingGhostEvent.ySpan}
            color={state.draggingGhostEvent.color}
            name={state.draggingGhostEvent.name}
            icon={
              state.draggingGhostEvent.behavior === Behavior.Work && (
                <ServiceIcon name="check-circle" />
              )
            }
          />
        )}
      {state.draggingEvent && state.draggingEvent.isAllDay && (
        <DragEventCell
          eventId={state.draggingEvent.uid}
          x={state.draggingEvent.x}
          xSpan={state.draggingEvent.xSpan}
          y={state.draggingEvent.y}
          ySpan={state.draggingEvent.ySpan}
          color={state.draggingEvent.color}
          name={state.draggingEvent.name}
          icon={
            state.draggingEvent.behavior === Behavior.Work && (
              <ServiceIcon name="check-circle" />
            )
          }
        />
      )}
      {!state.isLoading &&
        state.events.map((event, i) => (
          <StyledEventCell
            key={event.layoutId}
            className={
              event.uid === 'new-event'
                ? `${calendarNewEventPrefix}`
                : 'js-allday-events'
            }
            isDraggable
            eventId={event.uid}
            isDragging={event.uid === state.draggingEvent?.uid}
            x={event.x}
            y={event.y}
            xSpan={event.xSpan}
            ySpan={event.ySpan}
            color={event.color}
            name={event.name}
            onDragStart={actions.onEventDragStart}
            onDragEnd={actions.onEventDragEnd}
            onClick={actions.onClickEvent}
            onDragHandlerMove={actions.onEventDragHandlerMove}
            isSelected={
              props.selectedEventId === event.uid || event.uid === 'new-event'
            }
            responseStatus={event.responseStatus}
            icon={
              event.behavior === Behavior.Work && (
                <ServiceIcon name="check-circle" />
              )
            }
          />
        ))}

      {/* {state.creatingEvent && state.creatingEvent.isAllDay && (
        <NewEventCell
          isDraggable
          className={`${calendarNewEventPrefix}${state.creatingEvent.x!}-${state
            .creatingEvent.y!}`}
          eventId={'new-allday-event'}
          data-event-id={'new-allday-event'}
          isDragging={'new-allday-event' === state.draggingEvent?.eventId}
          x={state.creatingEvent.x!}
          y={state.creatingEvent.y!}
          ySpan={state.creatingEvent.ySpan!}
          color={state.creatingEvent.color}
          name={state.creatingEvent.name}
          onDragStart={actions.onEventDragStart}
          onDragEnd={actions.onEventDragEnd}
        />
      )} */}

      {Array.from({ length: state.maxRow + 1 }).map((_, i) => {
        return state.dates.map((date, frameId) => (
          <HeaderCell
            key={`${date.toMillis()}-${i}`}
            isHoliday={[0, 6].includes(date.weekday % 7)}
          >
            <DraggableCell
              draggable
              data-frame-id={frameId}
              data-all-day={1}
              data-slot-id={i}
              onDrop={actions.onEventDrop}
              onDragEnter={actions.onEventDragEnter}
              onDragOver={actions.onEventDragOver}
              onClick={actions.onClickAddSlot}
              // onDragStart={actions.onDayDragStart}
              // onDragEnter={
              //   state.draggingEvent
              //     ? actions.onEventDragEnter
              //     : actions.onDayDragEnter
              // }
              // onDragOver={actions.onEventDragOver}
              // onDragEnd={actions.onDayDragEnd}
              // onClick={actions.onClickAddSlot}
            />
          </HeaderCell>
        ));
      })}
    </Content>
  );
});
const Content = styled.div<{
  height?: number;
  maxRows: number;
  maxCols: number;
}>`
  width: inherit;
  flex: 1 1 0%;
  display: grid;
  grid-template-columns: ${(props) =>
    `repeat(${props.maxCols}, minmax(${props.height || 24}px, 1fr))`};
  grid-template-rows: ${(props) =>
    `repeat(${props.maxRows}, minmax(18px, 1fr))`};
  grid-auto-flow: row;
  position: relative;
`;
const HeaderCell = styled.div<{
  iconUrl?: string | null;
  isDisabled?: boolean;
  isHoliday?: boolean;
}>`
  border-left: 1px solid ${gray04};
  background-color: ${(props) => props.isHoliday && gray06};
  color: ${(props) => (props.isHoliday ? gray02 : gray01)};
  cursor: ${(props) => props.isDisabled && 'not-allowed'};
`;
const GhostEventCell = styled(AllDayEventCell)`
  opacity: 0.5;
  user-select: none;
  pointer-events: none;
`;
const DragEventCell = styled(AllDayEventCell)`
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.15);
  z-index: 3;
  user-select: none;
  pointer-events: none;
`;
const StyledEventCell = styled(AllDayEventCell)`
  z-index: 2;
`;
const NewEventCell = styled(AllDayEventCell)`
  z-index: 3;
`;
const ServiceIcon = styled(Icon)`
  width: 12px !important;
  height: 12px !important;
  color: inherit;
  margin-right: ${spacing(2)};
  :last-of-type {
    margin-right: 0;
  }
`;
export default Component;
